import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, CheckCircle, ArrowSquareOut, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-400">{label}</p>
        <p className="text-sm text-slate-800 break-words mt-0.5">{value}</p>
      </div>
      <button onClick={copy} data-testid={`copy-field-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="shrink-0 text-slate-400 hover:text-[#0055FF] transition-colors">
        {copied ? <CheckCircle weight="fill" size={18} className="text-emerald-500" /> : <Copy size={18} />}
      </button>
    </div>
  );
};

export default function ContentGenerator({ businessId, directoryId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  useEffect(() => {
    if (!open || !directoryId) return;
    setLoading(true);
    api.get(`/businesses/${businessId}/directories/${directoryId}/content`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load content"))
      .finally(() => setLoading(false));
  }, [open, directoryId, businessId]);

  const copyAll = () => {
    if (!data) return;
    navigator.clipboard.writeText(data.paste_block);
    setCopiedAll(true);
    toast.success("Full listing copied to clipboard");
    setTimeout(() => setCopiedAll(false), 1600);
  };

  const dir = data?.directory;
  const overLimit = data && data.desc_length >= (data.desc_limit || 9999);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="content-generator-sheet">
        <SheetHeader className="text-left">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#0055FF]">Ready-to-paste listing</p>
          <SheetTitle className="font-display text-2xl font-bold tracking-tight">{dir?.name || "Loading…"}</SheetTitle>
          <SheetDescription>{dir?.notes || "Copy each field or the full block below into the directory's submission form."}</SheetDescription>
        </SheetHeader>

        {loading || !data ? (
          <div className="py-16 flex justify-center">
            <div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-[#0055FF] animate-spin" />
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {dir?.submit_url && (
              <a href={dir.submit_url} target="_blank" rel="noreferrer" data-testid="directory-submit-link"
                className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3 hover:border-[#0055FF] hover:bg-slate-50 transition-colors group">
                <span className="text-sm font-semibold text-slate-800">Open submission page</span>
                <ArrowSquareOut size={18} className="text-slate-400 group-hover:text-[#0055FF]" />
              </a>
            )}

            <div className="rounded-md border border-slate-200 px-4">
              <CopyRow label="Business Name" value={data.fields.business_name} />
              <CopyRow label="Category" value={data.fields.category} />
              <CopyRow label="Address" value={data.fields.full_address} />
              <CopyRow label="Phone" value={data.fields.phone} />
              <CopyRow label="Website" value={data.fields.website} />
              <CopyRow label="Email" value={data.fields.email} />
              <CopyRow label="Hours" value={data.fields.hours} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">Description</p>
                <span className={`text-xs font-mono ${overLimit ? "text-red-600" : "text-slate-400"}`}>
                  {data.desc_length}/{data.desc_limit}
                </span>
              </div>
              <div className="rounded-md bg-slate-900 text-slate-100 p-4 font-mono text-sm leading-relaxed whitespace-pre-wrap">
                {data.fields.description || <span className="text-slate-500">No description added yet.</span>}
              </div>
              {overLimit && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600"><Warning size={14} /> Trimmed to fit this directory's limit.</p>
              )}
            </div>

            <div className="rounded-md bg-slate-900 p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Full listing block</p>
                <Button size="sm" onClick={copyAll} data-testid="copy-all-button"
                  className="h-8 bg-[#0055FF] hover:bg-[#0044CC] text-white">
                  {copiedAll ? <><CheckCircle weight="fill" size={15} className="mr-1" /> Copied</> : <><Copy size={15} className="mr-1" /> Copy all</>}
                </Button>
              </div>
              <pre className="font-mono text-xs text-slate-100 whitespace-pre-wrap leading-relaxed">{data.paste_block}</pre>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
